import { Calendar, Decoration, Event, Group, GroupMember, ReminderRule, ReminderSetting, StickerAsset, Theme, Todo, TodoCategory, TodoCompletionRecord, TodoDailyRecord, TodoDiaryEntry, TodoGoal, TodoPostponeRecord, TodoReaction, TodoRoutine, TodoTimerSession, User } from './models';
import { OWNIT_DEFAULT_FONT_ID, normalizeOwnitFontId } from './fonts';

export const OWNIT_DATA_VERSION = 13;
export const DEFAULT_GUEST_USER_ID = 'guest';
export const OWNIT_DEFAULT_THEME_ID = 'ownit-white';
export const OWNIT_CLOUD_DANCER_THEME_ID = 'cloud-dancer';
export const OWNIT_COSMIC_LATTE_THEME_ID = 'garden-green';
export const OWNIT_WHITE_BACKGROUND = '#ffffff';
export const OWNIT_EMPTY_TEXTURE = 'linear-gradient(transparent, transparent)';
export const OWNIT_NEUTRAL_COLORS = {
    text: '#2F2A25',
    accent: '#4F483F',
    personal: '#6B6259',
    shared: '#8A7880',
    mauve: '#8A7880',
    red: '#A65A4F',
    ochre: '#B59A5C',
    stone: '#73706A',
    rose: '#9A7B72',
    softFill: '#F7F7F6',
    softFillHover: '#F2F1EF',
    border: '#E6E4E1',
    borderStrong: '#DAD7D2',
    cosmicLatte: '#FFF8E7',
    cosmicLatteSoft: '#F6F1E8',
    cosmicLatteMuted: '#EEEAE3',
    cloudDancer: '#F0EEE9'
} as const;

export function neutralizeLegacyOwnitColor(color: string): string {
    const normalized = (color || '').trim().toLowerCase();
    const legacyMap: Record<string, string> = {
        '#2f8f6b': OWNIT_NEUTRAL_COLORS.personal,
        '#2f7d62': OWNIT_NEUTRAL_COLORS.personal,
        '#4d82d8': OWNIT_NEUTRAL_COLORS.shared,
        '#547fb7': OWNIT_NEUTRAL_COLORS.shared,
        '#3c6ead': OWNIT_NEUTRAL_COLORS.shared,
        '#2d5f9a': OWNIT_NEUTRAL_COLORS.shared,
        '#3b7a7f': OWNIT_NEUTRAL_COLORS.stone,
        '#f0785f': OWNIT_NEUTRAL_COLORS.red,
        '#d96f5f': OWNIT_NEUTRAL_COLORS.red,
        '#d6a94f': OWNIT_NEUTRAL_COLORS.ochre,
        '#f5c85b': OWNIT_NEUTRAL_COLORS.ochre,
        '#c45f7a': OWNIT_NEUTRAL_COLORS.rose
    };
    return legacyMap[normalized] || color;
}

export function createDateKey(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
}

export function createMonthKey(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    return `${year}-${month}`;
}

export function addDays(date: Date, amount: number): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + amount);
}

function createLocalReminderAt(dateKey: string, time: string): string {
    const [year, month, day] = dateKey.split('-').map((value) => Number(value));
    const [hour, minute] = time.split(':').map((value) => Number(value));
    return new Date(year, month - 1, day, hour || 0, minute || 0, 0, 0).toISOString();
}

function createReminderRule(userId: string, kind: ReminderRule['kind'], date: string | null, time: string, nextAt: string | null): ReminderRule {
    return {
        kind,
        date,
        time,
        nextAt,
        timezone: 'local',
        userId,
        enabled: true
    };
}

export function createEmptyUser(date = new Date()): User {
    const now = date.toISOString();
    return {
        id: '',
        username: '',
        email: '',
        authProvider: 'email',
        role: 'owner',
        name: '',
        nickname: '',
        profileImage: '',
        defaultFont: OWNIT_DEFAULT_FONT_ID,
        defaultTheme: OWNIT_DEFAULT_THEME_ID,
        createdAt: now,
        updatedAt: now
    };
}

export function normalizeDataUser(user: User, date = new Date()): User {
    const now = date.toISOString();
    const id = user.id || DEFAULT_GUEST_USER_ID;
    const email = user.email || '';
    const username = (user.username || email || id).trim();
    return {
        ...user,
        id,
        username,
        email,
        authProvider: user.authProvider || 'email',
        role: user.role || 'owner',
        defaultFont: normalizeOwnitFontId(user.defaultFont),
        defaultTheme: user.defaultTheme || OWNIT_DEFAULT_THEME_ID,
        createdAt: user.createdAt || now,
        updatedAt: user.updatedAt || user.createdAt || now
    };
}

export function createDefaultThemes(date = new Date()): Theme[] {
    const now = date.toISOString();
    return [
        {
            id: OWNIT_DEFAULT_THEME_ID,
            label: '화이트 기본',
            value: OWNIT_WHITE_BACKGROUND,
            background: OWNIT_WHITE_BACKGROUND,
            surface: OWNIT_WHITE_BACKGROUND,
            text: OWNIT_NEUTRAL_COLORS.text,
            accent: OWNIT_NEUTRAL_COLORS.accent,
            fontFamily: OWNIT_DEFAULT_FONT_ID,
            createdAt: now,
            updatedAt: now
        },
        {
            id: OWNIT_CLOUD_DANCER_THEME_ID,
            label: 'Cloud Dancer',
            value: OWNIT_NEUTRAL_COLORS.cloudDancer,
            background: OWNIT_NEUTRAL_COLORS.cloudDancer,
            surface: '#fbfaf7',
            text: OWNIT_NEUTRAL_COLORS.text,
            accent: OWNIT_NEUTRAL_COLORS.personal,
            fontFamily: OWNIT_DEFAULT_FONT_ID,
            createdAt: now,
            updatedAt: now
        },
        {
            id: OWNIT_COSMIC_LATTE_THEME_ID,
            label: '코스믹 라떼',
            value: OWNIT_NEUTRAL_COLORS.cosmicLatte,
            background: OWNIT_NEUTRAL_COLORS.cosmicLatte,
            surface: OWNIT_NEUTRAL_COLORS.cosmicLatteSoft,
            text: OWNIT_NEUTRAL_COLORS.text,
            accent: OWNIT_NEUTRAL_COLORS.personal,
            fontFamily: OWNIT_DEFAULT_FONT_ID,
            createdAt: now,
            updatedAt: now
        }
    ];
}

export function createEmptyGroup(user: User, date = new Date()): Group {
    const normalizedUser = normalizeDataUser(user, date);
    const now = date.toISOString();
    const groupId = '';
    return {
        id: groupId,
        name: '공유 그룹 없음',
        adminId: normalizedUser.id,
        inviteCode: '',
        inviteLink: '',
        members: [createOwnerGroupMember(normalizedUser, groupId, now)],
        archivedAt: null,
        deletedAt: null,
        createdAt: now,
        updatedAt: now
    };
}

export function createDefaultCalendars(user: User, date = new Date()): Calendar[] {
    const normalizedUser = normalizeDataUser(user, date);
    const now = date.toISOString();
    return [
        {
            id: `cal-${normalizedUser.id}-personal`,
            name: '기본 개인 캘린더',
            type: 'personal',
            ownerId: normalizedUser.id,
            groupId: null,
            color: OWNIT_NEUTRAL_COLORS.personal,
            description: '회원가입 시 자동 생성되는 개인 기본 캘린더입니다.',
            visibility: 'private',
            isDefault: true,
            isVisible: true,
            order: 0,
            archivedAt: null,
            deletedAt: null,
            createdAt: now,
            updatedAt: now
        }
    ];
}

function createOwnerGroupMember(user: User, groupId: string, joinedAt: string): GroupMember {
    const userRole: GroupMember['role'] = user.role === 'admin' ? 'admin' : 'owner';

    return {
        id: `member-${user.id}-owner`,
        groupId,
        userId: user.id,
        name: user.nickname || user.name || '나',
        nickname: user.nickname || user.name || '나',
        email: user.email || '',
        role: userRole,
        status: 'active',
        joinedAt
    };
}

function createDevelopmentGroupMembers(user: User, groupId: string, joinedAt: string): GroupMember[] {
    return [createOwnerGroupMember(user, groupId, joinedAt)];
}

export function createEmptyDecoration(user: User, date = new Date()): Decoration {
    const normalizedUser = normalizeDataUser(user, date);
    const now = date.toISOString();
    return {
        id: `deco-${normalizedUser.id}-${createMonthKey(date)}`,
        userId: normalizedUser.id,
        targetType: 'month',
        target: createMonthKey(date),
        background: 'white',
        themeId: normalizedUser.defaultTheme || OWNIT_DEFAULT_THEME_ID,
        fontId: normalizeOwnitFontId(normalizedUser.defaultFont),
        stickers: [],
        createdAt: now,
        updatedAt: now
    };
}

export function createSampleData(user: User, anchorDate = new Date()) {
    const normalizedUser = normalizeDataUser(user, anchorDate);
    const now = anchorDate.toISOString();
    const today = createDateKey(anchorDate);
    const minusThree = createDateKey(addDays(anchorDate, -3));
    const tomorrow = createDateKey(addDays(anchorDate, 1));
    const plusTwo = createDateKey(addDays(anchorDate, 2));
    const plusFive = createDateKey(addDays(anchorDate, 5));
    const plusSeven = createDateKey(addDays(anchorDate, 7));
    const plusNine = createDateKey(addDays(anchorDate, 9));
    const routineReminderAt = createLocalReminderAt(today, '09:00');
    const personalCalendarId = `cal-${normalizedUser.id}-personal`;
    const sharedCalendarId = `cal-${normalizedUser.id}-center`;
    const groupId = `group-${normalizedUser.id}-center`;
    const reminder30: ReminderSetting = { id: `reminder-${normalizedUser.id}-30`, offsetMinutes: 30, channel: 'app', enabled: true };
    const reminder10: ReminderSetting = { id: `reminder-${normalizedUser.id}-10`, offsetMinutes: 10, channel: 'app', enabled: true };

    const calendars: Calendar[] = [
        ...createDefaultCalendars(normalizedUser, anchorDate),
        {
            id: sharedCalendarId,
            name: '[개발용] 공유 캘린더',
            type: 'shared',
            ownerId: normalizedUser.id,
            groupId,
            color: OWNIT_NEUTRAL_COLORS.shared,
            description: 'demo=1에서만 생성되는 개발용 공유 캘린더입니다.',
            visibility: 'group',
            isDefault: false,
            isVisible: true,
            order: 100,
            archivedAt: null,
            deletedAt: null,
            createdAt: now,
            updatedAt: now
        }
    ];
    const todoCategories: TodoCategory[] = [
        {
            id: `todo-category-${normalizedUser.id}-plan`,
            userId: normalizedUser.id,
            name: '계획',
            color: OWNIT_NEUTRAL_COLORS.personal,
            icon: '',
            marker: 'dot',
            visibility: 'private',
            order: 0,
            sortOrder: 0,
            collapsed: false,
            locked: false,
            archived: false,
            archivedAt: null,
            ownerId: normalizedUser.id,
            createdAt: now,
            updatedAt: now
        },
        {
            id: `todo-category-${normalizedUser.id}-routine`,
            userId: normalizedUser.id,
            name: '루틴',
            color: OWNIT_NEUTRAL_COLORS.ochre,
            icon: '',
            marker: 'dot',
            visibility: 'private',
            order: 1,
            sortOrder: 1,
            collapsed: false,
            locked: false,
            archived: false,
            archivedAt: null,
            ownerId: normalizedUser.id,
            createdAt: now,
            updatedAt: now
        },
        {
            id: `todo-category-${normalizedUser.id}-record`,
            userId: normalizedUser.id,
            name: '기록',
            color: OWNIT_NEUTRAL_COLORS.red,
            icon: '',
            marker: 'dot',
            visibility: 'private',
            order: 2,
            sortOrder: 2,
            collapsed: false,
            locked: false,
            archived: false,
            archivedAt: null,
            ownerId: normalizedUser.id,
            createdAt: now,
            updatedAt: now
        }
    ];
    const todoGoals: TodoGoal[] = [
        {
            id: `todo-goal-${normalizedUser.id}-daily-plan`,
            userId: normalizedUser.id,
            name: '하루 계획',
            color: OWNIT_NEUTRAL_COLORS.personal,
            visibility: 'private',
            order: 0,
            collapsed: false,
            locked: false,
            ownerId: normalizedUser.id,
            categoryId: todoCategories[0].id,
            status: 'active',
            createdAt: now,
            updatedAt: now
        },
        {
            id: `todo-goal-${normalizedUser.id}-routine`,
            userId: normalizedUser.id,
            name: '루틴',
            color: OWNIT_NEUTRAL_COLORS.ochre,
            visibility: 'private',
            order: 1,
            collapsed: false,
            locked: false,
            ownerId: normalizedUser.id,
            categoryId: todoCategories[1].id,
            status: 'active',
            createdAt: now,
            updatedAt: now
        }
    ];
    const todoRoutines: TodoRoutine[] = [
        {
            id: `todo-routine-${normalizedUser.id}-morning-plan`,
            userId: normalizedUser.id,
            title: '아침 일정 정리',
            goalId: todoGoals[1].id,
            categoryId: todoCategories[1].id,
            color: OWNIT_NEUTRAL_COLORS.ochre,
            repeatType: 'weekly',
            repeatDays: [1, 2, 3, 4, 5],
            repeatDates: [],
            startDate: today,
            endDate: null,
            reminder: { id: `reminder-${normalizedUser.id}-routine-morning`, offsetMinutes: 0, channel: 'app', enabled: true },
            reminderAt: routineReminderAt,
            reminderRule: createReminderRule(normalizedUser.id, 'routine', null, '09:00', routineReminderAt),
            hideToday: false,
            visibility: 'private',
            ownerId: normalizedUser.id,
            assigneeId: normalizedUser.id,
            assignedTo: normalizedUser.id,
            scope: 'personal',
            repeatRule: { frequency: 'weekly', interval: 1, byWeekday: [1, 2, 3, 4, 5], until: null, count: null },
            reminders: [{ id: `reminder-${normalizedUser.id}-routine-morning`, offsetMinutes: 0, channel: 'app', enabled: true }],
            memo: '평일 아침마다 오늘 일정과 todo를 정리합니다.',
            createdAt: now,
            updatedAt: now
        }
    ];

    const group: Group = {
        id: groupId,
        name: '[개발용] 공유 그룹',
        adminId: normalizedUser.id,
        inviteCode: `DEV-${normalizedUser.id}`.slice(0, 16).toUpperCase(),
        inviteLink: `https://ownit.local/dev-seed/${normalizedUser.id}`,
        members: createDevelopmentGroupMembers(normalizedUser, groupId, now),
        archivedAt: null,
        deletedAt: null,
        createdAt: now,
        updatedAt: now
    };

    const events: Event[] = [
        {
            id: `event-${normalizedUser.id}-weekly-routine`,
            title: '주간 루틴 점검',
            description: '지난 todo 완료율과 이번 주 루틴 후보를 확인합니다.',
            startDate: minusThree,
            endDate: minusThree,
            startTime: '09:30',
            endTime: '10:00',
            allDay: false,
            color: OWNIT_NEUTRAL_COLORS.ochre,
            category: '루틴',
            calendarId: personalCalendarId,
            authorId: normalizedUser.id,
            assigneeId: normalizedUser.id,
            visibility: 'private',
            repeatRule: null,
            reminders: [reminder10],
            completed: false,
            createdAt: now,
            updatedAt: now
        },
        {
            id: `event-${normalizedUser.id}-month-review`,
            title: '월간 일정 정리',
            description: '이번 달 개인 일정과 공유 일정을 함께 점검합니다.',
            startDate: today,
            endDate: plusTwo,
            startTime: null,
            endTime: null,
            allDay: true,
            color: OWNIT_NEUTRAL_COLORS.personal,
            category: '개인',
            calendarId: personalCalendarId,
            authorId: normalizedUser.id,
            assigneeId: normalizedUser.id,
            visibility: 'private',
            repeatRule: null,
            reminders: [reminder30],
            completed: false,
            createdAt: now,
            updatedAt: now
        },
        {
            id: `event-${normalizedUser.id}-center-briefing`,
            title: '센터 공유 브리핑',
            description: '오늘 담당자와 공유 업무를 확인합니다.',
            startDate: tomorrow,
            endDate: tomorrow,
            startTime: '10:00',
            endTime: '11:00',
            allDay: false,
            color: OWNIT_NEUTRAL_COLORS.shared,
            category: '공유',
            calendarId: sharedCalendarId,
            authorId: normalizedUser.id,
            assigneeId: normalizedUser.id,
            visibility: 'group',
            repeatRule: null,
            reminders: [reminder10],
            completed: false,
            createdAt: now,
            updatedAt: now
        },
        {
            id: `event-${normalizedUser.id}-theme-day`,
            title: '꾸미기 테마 선택',
            description: '월간 캘린더 기본 색상과 스티커 톤을 정합니다.',
            startDate: plusFive,
            endDate: plusFive,
            startTime: '16:00',
            endTime: '16:30',
            allDay: false,
            color: OWNIT_NEUTRAL_COLORS.red,
            category: '꾸미기',
            calendarId: personalCalendarId,
            authorId: normalizedUser.id,
            assigneeId: normalizedUser.id,
            visibility: 'private',
            repeatRule: null,
            reminders: [{ id: `reminder-${normalizedUser.id}-60`, offsetMinutes: 60, channel: 'app', enabled: true }],
            completed: false,
            createdAt: now,
            updatedAt: now
        },
        {
            id: `event-${normalizedUser.id}-center-workshop`,
            title: '센터 운영 워크숍',
            description: '공유 일정과 담당자 todo 흐름을 함께 정리합니다.',
            startDate: plusSeven,
            endDate: plusNine,
            startTime: null,
            endTime: null,
            allDay: true,
            color: OWNIT_NEUTRAL_COLORS.shared,
            category: '공유',
            calendarId: sharedCalendarId,
            authorId: normalizedUser.id,
            assigneeId: normalizedUser.id,
            visibility: 'group',
            repeatRule: null,
            reminders: [reminder30],
            completed: false,
            createdAt: now,
            updatedAt: now
        }
    ];

    const todos: Todo[] = [
        {
            id: `todo-${normalizedUser.id}-plan`,
            title: '오늘 일정 3개만 먼저 정리하기',
            date: today,
            completed: true,
            completedAt: now,
            goalId: todoGoals[0].id,
            categoryId: todoCategories[0].id,
            routineId: todoRoutines[0].id,
            category: '계획',
            color: OWNIT_NEUTRAL_COLORS.personal,
            order: 0,
            authorId: normalizedUser.id,
            assigneeId: normalizedUser.id,
            createdBy: normalizedUser.id,
            assignedTo: normalizedUser.id,
            scope: 'personal',
            visibility: 'private',
            repeatRule: { frequency: 'weekly', interval: 1, byWeekday: [1, 2, 3, 4, 5], until: null, count: null },
            reminders: [{ id: `reminder-${normalizedUser.id}-todo-plan`, offsetMinutes: 0, channel: 'app', enabled: true }],
            reminderAt: null,
            reminderRule: null,
            memo: '아침 루틴으로 반복할 후보',
            movedFromDate: null,
            movedToDate: null,
            timerSessionIds: [],
            photoProofUrl: '',
            timerSeconds: 0,
            diaryId: null,
            reactionId: null,
            completionRecordId: `todo-completion-${normalizedUser.id}-plan-${today}`,
            createdAt: now,
            updatedAt: now
        },
        {
            id: `todo-${normalizedUser.id}-share`,
            title: '센터 공유 업무 담당자 확인',
            date: today,
            completed: false,
            completedAt: null,
            goalId: null,
            categoryId: null,
            routineId: null,
            category: '공유',
            color: OWNIT_NEUTRAL_COLORS.shared,
            order: 1,
            authorId: normalizedUser.id,
            assigneeId: normalizedUser.id,
            createdBy: normalizedUser.id,
            assignedTo: normalizedUser.id,
            groupId,
            scope: 'shared',
            visibility: 'group',
            repeatRule: null,
            reminders: [reminder30],
            reminderAt: null,
            reminderRule: null,
            memo: '공유 탭과 연결 예정',
            movedFromDate: null,
            movedToDate: null,
            timerSessionIds: [],
            photoProofUrl: '',
            timerSeconds: 0,
            diaryId: null,
            reactionId: null,
            completionRecordId: null,
            createdAt: now,
            updatedAt: now
        },
        {
            id: `todo-${normalizedUser.id}-receipt`,
            title: '하루 기록 영수증에 들어갈 항목 메모',
            date: tomorrow,
            completed: false,
            completedAt: null,
            goalId: null,
            categoryId: todoCategories[2].id,
            routineId: null,
            category: '기록',
            color: OWNIT_NEUTRAL_COLORS.ochre,
            order: 0,
            authorId: normalizedUser.id,
            assigneeId: normalizedUser.id,
            createdBy: normalizedUser.id,
            assignedTo: normalizedUser.id,
            scope: 'personal',
            visibility: 'private',
            repeatRule: null,
            reminders: [],
            reminderAt: null,
            reminderRule: null,
            memo: '완료/미완료 todo 분리 저장',
            movedFromDate: null,
            movedToDate: null,
            timerSessionIds: [],
            photoProofUrl: '',
            timerSeconds: 0,
            diaryId: null,
            reactionId: null,
            completionRecordId: null,
            createdAt: now,
            updatedAt: now
        }
    ];
    const todoDailyRecords: TodoDailyRecord[] = [
        {
            id: `todo-daily-${normalizedUser.id}-${today}`,
            userId: normalizedUser.id,
            date: today,
            completedCount: 1,
            totalCount: 2,
            categoryStats: [
                {
                    categoryId: todoCategories[0].id,
                    goalId: todoGoals[0].id,
                    name: '계획',
                    color: OWNIT_NEUTRAL_COLORS.personal,
                    completedCount: 1,
                    totalCount: 1,
                    timerSeconds: 0
                },
                {
                    categoryId: null,
                    goalId: null,
                    name: '공유',
                    color: OWNIT_NEUTRAL_COLORS.shared,
                    completedCount: 0,
                    totalCount: 1,
                    timerSeconds: 0
                }
            ],
            representativeEmoji: '☁',
            diary: '',
            totalTimerSeconds: 0,
            receiptId: null,
            receiptSnapshot: null,
            createdAt: now,
            updatedAt: now
        }
    ];
    const todoCompletionRecords: TodoCompletionRecord[] = [
        {
            id: `todo-completion-${normalizedUser.id}-plan-${today}`,
            todoId: `todo-${normalizedUser.id}-plan`,
            routineId: todoRoutines[0].id,
            date: today,
            completed: true,
            completedAt: now,
            authorId: normalizedUser.id,
            reactionId: null,
            diaryId: null,
            createdAt: now,
            updatedAt: now
        }
    ];
    const todoPostponeRecords: TodoPostponeRecord[] = [];
    const todoTimerSessions: TodoTimerSession[] = [];
    const todoDiaries: TodoDiaryEntry[] = [];
    const todoReactions: TodoReaction[] = [];
    const stickerAssets: StickerAsset[] = [];

    const decorations: Decoration[] = [
        {
            id: `deco-${normalizedUser.id}-${createMonthKey(anchorDate)}`,
            userId: normalizedUser.id,
            targetType: 'month',
            target: createMonthKey(anchorDate),
            background: 'white',
            themeId: normalizedUser.defaultTheme || OWNIT_DEFAULT_THEME_ID,
            fontId: normalizeOwnitFontId(normalizedUser.defaultFont),
            stickers: [
                { id: `sticker-${normalizedUser.id}-sun`, stickerId: 'emoji-weather-sun', label: '맑음', target: 'calendar', date: null, x: 18, y: 22, size: 40, rotation: -8, zIndex: 1 },
                { id: `sticker-${normalizedUser.id}-party`, stickerId: 'emoji-celebration-party', label: '축하', target: 'date', date: today, x: 72, y: 60, size: 30, rotation: 6, zIndex: 2 }
            ],
            createdAt: now,
            updatedAt: now
        }
    ];

    return {
        user: normalizedUser,
        calendars,
        groups: [group],
        events,
        todos,
        todoCategories,
        todoGoals,
        todoRoutines,
        todoDailyRecords,
        todoCompletionRecords,
        todoPostponeRecords,
        todoTimerSessions,
        todoDiaries,
        todoReactions,
        stickerAssets,
        decorations,
        themes: createDefaultThemes(anchorDate)
    };
}
